import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Box, Chip, Alert
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function configStatus(config) {
  const now = new Date();
  if (!config.is_active) return { label: 'Inactive', color: 'default' };
  if (new Date(config.effective_at) > now) return { label: 'Scheduled', color: 'warning' };
  if (config.expires_at && new Date(config.expires_at) <= now) return { label: 'Expired', color: 'default' };
  return { label: 'Active', color: 'success' };
}

export default function ProviderFeeManagement() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({
    provider: 'stripe', percentage: '2.9', fixed_cents: '30',
    international_additional_pct: '1.0', dispute_fee_cents: '1500',
    refund_fee_pct: '0', effective_at: '', description: ''
  });

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const data = await billingApi.get('/api/admin/fee-configs');
      setConfigs(data.configs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      setError('');
      if (!form.effective_at) {
        setError('Effective date is required');
        return;
      }
      await billingApi.post('/api/admin/fee-configs', {
        ...form,
        percentage: parseFloat(form.percentage),
        fixed_cents: parseInt(form.fixed_cents),
        international_additional_pct: parseFloat(form.international_additional_pct),
        dispute_fee_cents: parseInt(form.dispute_fee_cents),
        refund_fee_pct: parseFloat(form.refund_fee_pct),
      });
      setDialog(false);
      setSuccess('Fee configuration created');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  const columns = useMemo(() => [
    {
      field: 'provider',
      headerName: 'Provider',
      width: 120,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
          {row.provider}
        </Typography>
      ),
    },
    {
      field: 'percentage',
      headerName: 'Rate %',
      width: 100,
      type: 'numeric',
      renderCell: (row) => `${parseFloat(row.percentage).toFixed(2)}%`,
    },
    {
      field: 'fixed_cents',
      headerName: 'Fixed Fee',
      width: 100,
      type: 'numeric',
      renderCell: (row) => `$${(row.fixed_cents / 100).toFixed(2)}`,
    },
    {
      field: 'international_additional_pct',
      headerName: 'Intl +%',
      width: 90,
      type: 'numeric',
      renderCell: (row) => row.international_additional_pct ? `+${parseFloat(row.international_additional_pct).toFixed(1)}%` : '—',
    },
    {
      field: 'dispute_fee_cents',
      headerName: 'Dispute Fee',
      width: 110,
      type: 'numeric',
      renderCell: (row) => `$${(row.dispute_fee_cents / 100).toFixed(2)}`,
    },
    {
      field: 'effective_at',
      headerName: 'Effective From',
      width: 140,
      type: 'date',
      renderCell: (row) => formatDate(row.effective_at),
    },
    {
      field: 'expires_at',
      headerName: 'Expires',
      width: 140,
      type: 'date',
      renderCell: (row) => row.expires_at ? formatDate(row.expires_at) : '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      type: 'singleSelect',
      filterOptions: ['Active', 'Scheduled', 'Expired', 'Inactive'],
      valueGetter: ({ row }) => configStatus(row).label,
      renderCell: (row) => {
        const status = configStatus(row);
        return <Chip label={status.label} color={status.color} size="small" variant="outlined" />;
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 140,
      type: 'date',
      renderCell: (row) => formatDate(row.created_at),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'id',
      headerName: 'Config ID',
      width: 120,
      renderCell: (row) => (
        <Typography variant="caption" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.65rem' }}>
          {row.id?.substring(0, 12)}
        </Typography>
      ),
    },
  ], []);

  if (loading) {
    return <AdminPageSkeleton />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Provider Fees
        </Typography>
        <Button variant="contained" onClick={() => setDialog(true)}
          sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
          Add Fee Config
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        Fee configurations are versioned. Creating a new config for a provider automatically expires the previous one.
        Each payment's fee entry links to the config that was active at the time of the transaction.
      </Alert>

      <Paper variant="outlined" sx={{ height: 500 }}>
        <AdminTable
          rows={configs}
          columns={columns}
          defaultSort={[{ field: 'effective_at', direction: 'desc' }]}
          defaultPageSize={25}
        />
      </Paper>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Fee Configuration</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Provider" value={form.provider}
            onChange={e => setForm({ ...form, provider: e.target.value })}
            sx={{ mt: 1, mb: 2 }} helperText="e.g., stripe, paypal" />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="Rate %" type="number" value={form.percentage}
              onChange={e => setForm({ ...form, percentage: e.target.value })}
              sx={{ flex: 1 }} inputProps={{ step: '0.01' }}
              helperText="e.g., 2.9 for 2.9%" />
            <TextField label="Fixed Fee (cents)" type="number" value={form.fixed_cents}
              onChange={e => setForm({ ...form, fixed_cents: e.target.value })}
              sx={{ flex: 1 }} helperText="e.g., 30 for $0.30" />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField label="International +%" type="number" value={form.international_additional_pct}
              onChange={e => setForm({ ...form, international_additional_pct: e.target.value })}
              sx={{ flex: 1 }} inputProps={{ step: '0.1' }}
              helperText="Extra % for international cards" />
            <TextField label="Dispute Fee (cents)" type="number" value={form.dispute_fee_cents}
              onChange={e => setForm({ ...form, dispute_fee_cents: e.target.value })}
              sx={{ flex: 1 }} helperText="e.g., 1500 for $15" />
          </Box>

          <TextField fullWidth type="datetime-local" label="Effective From"
            InputLabelProps={{ shrink: true }}
            value={form.effective_at} onChange={e => setForm({ ...form, effective_at: e.target.value })}
            sx={{ mb: 2 }} />

          <TextField fullWidth label="Description" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            helperText="e.g., Stripe standard pricing (2.9% + $0.30)" />

          <Alert severity="warning" sx={{ mt: 2 }}>
            Creating a new config for an existing provider will automatically expire the current config
            at this config's effective date.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
